import { zodResolver } from "@hookform/resolvers/zod";
import React, { FC, useState, useRef, useMemo, useCallback } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useHistory } from "react-router";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

import {
  JobCategory,
  JobIndustry,
  MoneyAmount,
  Period,
  RegisteredStatusEnum,
  User,
  UserUpdate,
  UserUpdateWayToProposeEnum,
} from "@/api/generated";
import { LoadingOverlay } from "@/components/LoadingOverlay";
import { Toast } from "@/components/Toast";
import { DEFAULT_ACCOUNT_REGISTER_FORM_SCHEMA } from "@/constants/account";
import { PropallyApiFactory } from "@/module/CustomApiFactory";
import { sendEventToAppMeasurementTool, AppEventTokenEnum } from "@/utilities/capacitorLogic";
import { parseFormattedNumber } from "@/utilities/format";
import {
  getStorageAccountRegistrationState,
  getStorageAccountRegistrationUserId,
  setStorageAccountRegistrationState,
} from "@/utilities/storage";
import { accountRegisterSchema, AccountRegisterFormData } from "@/validations/account";
import { annualIncomeSchema, ANNUAL_INCOME_RANGE_MESSAGE, getAnnualIncomeError } from "@/validations/annualIncome";
import { createSelectRequiredMessage } from "@/validations/message";
import { UserDataFirstRegisteredStatusState, UserRegistrationsAccoutFormState, UserState } from "@/view_models/atoms";

import RegisterBirthYearMonthPage from "./components/Birth";
import RegisterFinancialAssetsPage from "./components/FinancialAssets";
import RegisterInterestPage from "./components/Interest";
import RegisterJobPage from "./components/Job";
import RegisterReasonPage from "./components/Reason";
import RegisterResidencePage from "./components/Residence";
import WelcomePage from "./components/Welcome";
import { getAccountPriorAnswers } from "./utils/priorAnswers";
import { getAccountStepFields, AccountFormStep, focusFirstRegistrationError } from "./utils/stepFields";

const JOB_INDUSTRY_REQUIRED_CATEGORIES = [
  JobCategory.会社員上場,
  JobCategory.会社員非上場,
  JobCategory.会社員外資系企業,
];

export const AccountRegisterBasicPage: FC = () => {
  const isSubmittingRef = useRef(false);
  const methods = useForm<AccountRegisterFormData>({
    resolver: zodResolver(accountRegisterSchema),
    mode: "onTouched",
    shouldUnregister: false,
    defaultValues: DEFAULT_ACCOUNT_REGISTER_FORM_SCHEMA,
  });
  const [currentStep, setCurrentStep] = useState(0);
  const setUser = useSetRecoilState(UserState);
  const user = useRecoilValue(UserState);
  const initializedUserIdRef = useRef<number | null>(null);
  const [priorAnswers, setPriorAnswers] = useState<Partial<AccountRegisterFormData> | null>(null);
  const [error, setError] = useState<string>("");
  const [incomeRecoveryRequested, setIncomeRecoveryRequested] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const setUserDataFirstRegisteredStatus = useSetRecoilState(UserDataFirstRegisteredStatusState);
  const [accountFormData, setAccountFormData] = useRecoilState(UserRegistrationsAccoutFormState);
  const history = useHistory();

  const validate = useCallback(
    async (step: AccountFormStep) => {
      const valuesBeforeValidation = methods.getValues();
      const fields = getAccountStepFields(step, valuesBeforeValidation);
      let isValid = await methods.trigger(fields);
      // 全体スキーマのsuperRefineは、未表示の必須項目がundefinedだと実行されない。
      // 登録再開でも、表示中の業種だけは既存の条件どおり必須にする。
      if (step === "job" && fields.includes("job_industry") && !valuesBeforeValidation.job_industry?.trim()) {
        methods.setError("job_industry", { type: "required", message: createSelectRequiredMessage("業種") });
        isValid = false;
      }
      if (isValid) {
        const values = methods.getValues();
        setAccountFormData((prev) => ({ ...prev, ...values }));
      } else focusFirstRegistrationError();
      return isValid;
    },
    [methods, setAccountFormData]
  );

  const handleBack = useCallback(() => {
    const values = methods.getValues();
    setAccountFormData((prev) => ({ ...prev, ...values }));
    setCurrentStep((prev) => prev - 1);
  }, [methods, setAccountFormData]);

  const requestIncomeCorrection = useCallback(
    (message: string) => {
      const draft = { ...accountFormData, ...methods.getValues(), annual_income: "" };
      methods.setValue("annual_income", "");
      setAccountFormData(draft);
      setStorageAccountRegistrationState(draft, user?.id);
      setPriorAnswers((answers) => ({ ...answers, annual_income: "" }));
      setError(message);
      setIncomeRecoveryRequested(true);
    },
    [accountFormData, methods, setAccountFormData, user?.id]
  );

  const onSubmit = useCallback(async () => {
    // 二重送信防止
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsSubmitting(true);
    // 遷移する場合は画面が切り替わるまでオーバーレイを出したままにするため、送信状態を戻さない
    let hasNavigated = false;
    try {
      const validate_result = await validate("reason");
      if (!validate_result) return;
      // if (typeof formData.real_estate_loan_amount === "string") {
      //   formData.real_estate_loan_amount = parseInt(formData.real_estate_loan_amount.replace(/,/g, ""));
      // }
      const values = methods.getValues();
      if (!annualIncomeSchema.safeParse(values.annual_income).success) {
        requestIncomeCorrection(ANNUAL_INCOME_RANGE_MESSAGE);
        return;
      }
      const sendValues: UserUpdate = {
        ...accountFormData,
        ...values, // 最後のフォームの値を送信するために追加
        financial_asset: accountFormData.financial_asset as MoneyAmount,
        job_category: accountFormData.job_category as JobCategory,
        job_industry: accountFormData.job_industry as JobIndustry,
        job_continuous_period: accountFormData.job_continuous_period as Period,
        birthday: `${accountFormData.birth_year}-${accountFormData.birth_month}-01`,
        current_residence_type_id: accountFormData.current_residence_type_id,
        home_loan_amount: parseFormattedNumber(accountFormData.home_loan_amount),
        real_estate_loan_amount: parseFormattedNumber(accountFormData.real_estate_loan_amount),
        monthly_rent_payment_amount: parseFormattedNumber(accountFormData.monthly_rent_payment_amount),
        annual_income: parseFormattedNumber(accountFormData.annual_income),
        holding_property_count: parseInt(accountFormData.holding_property_count),
        // MEMO: 以下「way_to_propose」「request_the_proposal」は現状、非表示項目となるので、固定値を送信する
        way_to_propose: UserUpdateWayToProposeEnum.NUMBER_0,
        request_the_proposal: true,
      };

      const fp = PropallyApiFactory();
      try {
        const userPatch = await fp.v1UserPatch(sendValues, {
          withCredentials: true,
        });
        if (userPatch.status === 200) {
          const updatedUser = userPatch.data as unknown as User;
          setUser(updatedUser);
          // 初回登録済みステータスを更新
          await fp
            .v1UserDataFirstRegisteredStatusPatch(
              {
                status: RegisteredStatusEnum.Account,
              },
              {
                withCredentials: true,
              }
            )
            .then((res) => {
              setUserDataFirstRegisteredStatus(res.data.status);
            });

          // エージェントに新着通知。登録完了の可否とは無関係なため完了を待たない。
          // 待つと遷移が遅れるうえ、通知だけ失敗した場合に登録が失敗したように見えてしまう
          fp.v1NotificationsToAgentForNewUserPost({ withCredentials: true }).catch(() => undefined);

          // ユーザアカウント登録完了イベント発火
          sendEventToAppMeasurementTool(AppEventTokenEnum.UserAccountCompleted);

          hasNavigated = true;
          if (updatedUser?.is_diagnosis_complete === false) {
            // 本登録完了 → 登録完了画面を経由してから投資力診断結果へ
            history.push("/registration_complete");
          } else {
            history.push({ pathname: "/mypage", state: { previousPath: location.pathname } });
          }
        }
      } catch (e) {
        const incomeError = getAnnualIncomeError(e);
        if (incomeError) {
          requestIncomeCorrection(incomeError);
          return;
        }
        if (e.response?.status === 401) {
          setError("更新に失敗しました。画面をリロードし、再度ログインしてください");
          return;
        } else if (e.response?.data?.code === "INVALID_PHONE_NUMBER_AUTH_CODE") {
          setError("電話番号認証コードが不正です");
          return;
        }
        if (e.code === "ERR_BAD_RESPONSE") {
          setError("通信結果を確認できませんでした。再読み込みして登録内容を確認してください。");
          return;
        }
        setError("更新に失敗しました。画面をリロードし、再度ログインしてください");
      }
    } finally {
      // 画面に留まる場合のみ状態を戻す。従来は失敗時の早期returnでフラグが戻らず、
      // 一度失敗するとボタンが反応しなくなっていた
      if (!hasNavigated) {
        isSubmittingRef.current = false;
        setIsSubmitting(false);
      }
    }
  }, [accountFormData, validate, methods, setUser, setUserDataFirstRegisteredStatus, history, requestIncomeCorrection]);

  React.useEffect(() => {
    if (!user || initializedUserIdRef.current === user.id) return;
    const owner = getStorageAccountRegistrationUserId();
    const canUseDraft = owner === null || owner === user.id;
    const stored = getStorageAccountRegistrationState(user.id);
    const draft = canUseDraft ? { ...accountFormData, ...stored } : {};
    const answers = getAccountPriorAnswers(user, draft);
    const nextValues: Partial<AccountRegisterFormData> = {
      address_prefecture: user.address_prefecture ?? "",
      current_residence_type_id: user.current_residence_type_id ?? null,
      home_loan_amount: user.home_loan_amount != null ? String(user.home_loan_amount) : "",
      monthly_rent_payment_amount:
        user.monthly_rent_payment_amount != null ? String(user.monthly_rent_payment_amount) : "",
      interest_tag_ids: user.interest_tag_ids ?? [],
      interested_property_type_ids: user.interested_property_type_ids ?? [],
      purpose_of_use_ids: user.purpose_of_use_ids ?? [],
      purpose_of_use_free_text: user.purpose_of_use_free_text ?? "",
      other_request_text: user.other_request_text ?? "",
      inflow_route_ids: user.inflow_route_ids ?? [],
      inflow_route_free_text: user.inflow_route_free_text ?? "",
      ...answers,
    };
    // 開始時の回答でステップを固定し、補入力を保存した直後にステップが消えるのを防ぐ。
    setPriorAnswers(answers);
    const nextDraft = { ...DEFAULT_ACCOUNT_REGISTER_FORM_SCHEMA, ...draft, ...nextValues };
    setStorageAccountRegistrationState(nextDraft, user.id);
    setAccountFormData(nextDraft);
    methods.reset({ ...DEFAULT_ACCOUNT_REGISTER_FORM_SCHEMA, ...nextValues });
    if (initializedUserIdRef.current !== null) setCurrentStep(0);
    initializedUserIdRef.current = user.id;
  }, [accountFormData, methods, setAccountFormData, user]);

  // ユーザーの既存値が「入力済み」と見なせるか判定するためのユーティリティ群
  const hasUserValue = useCallback((value: string | number | null | undefined) => value !== "" && value != null, []);
  // job / asset の完了判定用に、必須項目がすべて埋まっているかをまとめてチェックする
  const areAllUserValuesPresent = useCallback(
    (values: Array<string | number | null | undefined>) => values.every(hasUserValue),
    [hasUserValue]
  );
  // 特定の職種では業種入力が必須になるため、対象かどうかを判定する
  const userNeedsJobIndustry = useMemo(() => {
    if (!priorAnswers?.job_category || priorAnswers.job_category === JobCategory.入力なし) return false;
    return JOB_INDUSTRY_REQUIRED_CATEGORIES.includes(priorAnswers.job_category as JobCategory);
  }, [priorAnswers?.job_category]);
  const isUserJobCompleted = useMemo(() => {
    if (!priorAnswers) return false;
    if (!annualIncomeSchema.safeParse(priorAnswers.annual_income).success) return false;
    if (priorAnswers.job_category === JobCategory.入力なし) return false;
    const requiredValues: Array<string | number | null | undefined> = [
      priorAnswers.job_category,
      priorAnswers.job_continuous_period,
      priorAnswers.annual_income,
    ];
    if (userNeedsJobIndustry) {
      requiredValues.push(priorAnswers.job_industry);
    }
    return areAllUserValuesPresent(requiredValues);
  }, [areAllUserValuesPresent, priorAnswers, userNeedsJobIndustry]);
  // 保有物件数に応じて、借入額入力が必須かどうかを判定する
  const userHoldingPropertyCount = useMemo(() => {
    if (!priorAnswers || !hasUserValue(priorAnswers.holding_property_count)) return null;
    const parsed = Number(priorAnswers.holding_property_count);
    return Number.isNaN(parsed) ? null : parsed;
  }, [hasUserValue, priorAnswers]);
  const userNeedsLoanAmount = (userHoldingPropertyCount ?? 0) >= 1;
  const isUserAssetCompleted = useMemo(() => {
    if (!priorAnswers) return false;
    const requiredValues: Array<string | number | null | undefined> = [
      priorAnswers.financial_asset,
      priorAnswers.holding_property_count,
    ];
    if (userNeedsLoanAmount) {
      requiredValues.push(priorAnswers.real_estate_loan_amount);
    }
    return areAllUserValuesPresent(requiredValues);
  }, [areAllUserValuesPresent, priorAnswers, userNeedsLoanAmount]);

  const steps = useMemo(() => {
    const formSteps: Array<{
      key: string;
      token: AppEventTokenEnum;
      render: (stepNumber: number, totalSteps: number) => JSX.Element;
    }> = [];
    // MEMO: フェーズ2でステップ順を「名前 → 現在のお住まい」に変更（元は お住まい → 名前 の順だった）
    formSteps.push({
      key: "step1",
      token: AppEventTokenEnum.RegistrationDetailFirstAccess,
      render: (stepNumber, totalSteps) => (
        <RegisterBirthYearMonthPage
          key="step1"
          totalSteps={totalSteps}
          currentStep={stepNumber}
          onNext={() => setCurrentStep((prev) => prev + 1)}
          onBack={handleBack}
          validate={() => validate("birth")}
        />
      ),
    });
    formSteps.push({
      key: "step2",
      token: AppEventTokenEnum.RegistrationDetailSecondAccess,
      render: (stepNumber, totalSteps) => (
        <RegisterResidencePage
          key="step2"
          totalSteps={totalSteps}
          currentStep={stepNumber}
          onNext={() => setCurrentStep((prev) => prev + 1)}
          onBack={handleBack}
          validate={() => validate("residence")}
        />
      ),
    });
    if (!isUserJobCompleted) {
      formSteps.push({
        key: "step3",
        token: AppEventTokenEnum.RegistrationDetailThirdAccess,
        render: (stepNumber, totalSteps) => (
          <RegisterJobPage
            key="step3"
            totalSteps={totalSteps}
            currentStep={stepNumber}
            onNext={() => {
              setError("");
              setCurrentStep((prev) => prev + 1);
            }}
            onBack={handleBack}
            validate={() => validate("job")}
          />
        ),
      });
    }
    if (!isUserAssetCompleted) {
      formSteps.push({
        key: "step4",
        token: AppEventTokenEnum.RegistrationDetailFourthAccess,
        render: (stepNumber, totalSteps) => (
          <RegisterFinancialAssetsPage
            key="step4"
            totalSteps={totalSteps}
            currentStep={stepNumber}
            onNext={() => setCurrentStep((prev) => prev + 1)}
            onBack={handleBack}
            validate={() => validate("assets")}
          />
        ),
      });
    }
    formSteps.push({
      key: "step5",
      token: AppEventTokenEnum.RegistrationDetailFifthAccess,
      render: (stepNumber, totalSteps) => (
        <RegisterInterestPage
          key="step5"
          totalSteps={totalSteps}
          currentStep={stepNumber}
          onNext={() => setCurrentStep((prev) => prev + 1)}
          onBack={handleBack}
          validate={() => validate("interest")}
        />
      ),
    });
    formSteps.push({
      key: "step6",
      token: AppEventTokenEnum.RegistrationDetailSixthAccess,
      render: (stepNumber, totalSteps) => (
        <RegisterReasonPage
          key="step6"
          totalSteps={totalSteps}
          currentStep={stepNumber}
          onNext={() => onSubmit()}
          onBack={handleBack}
          validate={() => validate("reason")}
        />
      ),
    });

    const totalSteps = formSteps.length;
    return [
      {
        key: "step0",
        token: AppEventTokenEnum.RegistrationDetailTopAccess,
        element: <WelcomePage key="step0" onNext={() => setCurrentStep((prev) => prev + 1)} />,
      },
      ...formSteps.map((step, index) => ({
        key: step.key,
        token: step.token,
        element: step.render(index + 1, totalSteps),
      })),
    ];
  }, [isUserAssetCompleted, isUserJobCompleted, onSubmit, validate, handleBack]);

  React.useEffect(() => {
    if (!incomeRecoveryRequested) return;
    const index = steps.findIndex((step) => step.key === "step3");
    if (index < 0) return;
    setCurrentStep(index);
    setIncomeRecoveryRequested(false);
  }, [incomeRecoveryRequested, steps]);

  const currentToken = steps[currentStep]?.token;
  React.useEffect(() => {
    // ステップが切り替わったタイミングでAdjustイベントを発火
    const token = currentToken;
    if (token) {
      sendEventToAppMeasurementTool(token);
    }
  }, [currentToken]);

  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentStep]);

  // MEMO: 流入経路は非表示のため固定値設定。効果があるようであれば根本から変更予定。
  React.useEffect(() => {
    methods.setValue("inflow_route_ids", [6]);
    methods.setValue("inflow_route_free_text", "流入経路非表示中");
  }, []);

  // トーストのクローズ
  const handleClose = () => {
    setError("");
  };

  // WithAuthの配下のため、認証のチェックはしない
  return (
    <FormProvider {...methods}>
      <form onSubmit={(event) => event.preventDefault()}>{steps[currentStep]?.element}</form>
      {/* エラーメッセージ表示用 */}
      <Toast open={Boolean(error)} variant="error" message={error} onClose={handleClose} />
      <LoadingOverlay open={isSubmitting} />
    </FormProvider>
  );
};
